import {Component, Input, OnInit} from '@angular/core';
import {Router} from "@angular/router";
import {TranslocoService} from "@ngneat/transloco";
import {FormControl, FormGroup} from "@angular/forms";
import {NgbDateParserFormatter} from '@ng-bootstrap/ng-bootstrap';
import {environment} from "@environment";
import {
  adjustForTimezone,
  alphabetical,
  email,
  max_length_10,
  max_length_100,
  max_length_255,
  max_length_30,
  max_length_45,
  max_length_50,
  mediumTextValidator,
  required
} from '@utility';
import {AbstractComponent} from "@components";
import {Country, Lookups, Message, UserDetail, TOAST_STATE} from "@model";
import {LoaderService, ToastService, UserDetailService} from "@services";
import {MY_TRACEY} from "../../../../app-routing.module";
import {MyTraceyDataStore} from "@store";
import {finalize} from "rxjs/operators";

@Component({
    selector: 'app-user-profile-edit',
    templateUrl: './user-profile-edit.component.html',
    styleUrls: ['./user-profile-edit.component.scss'],
    standalone: false
})
export class UserProfileEditComponent extends AbstractComponent implements OnInit {

  public static readonly BIRTHDAY_YEAR_SHIFT: number = 18;

  @Input() currentUserDetails: UserDetail | undefined;
  @Input() countries: Country[] = [];
  @Input() genders: Lookups[] = [];
  @Input() meals: Lookups[] = [];
  @Input('sizes') tshirtSizes: Lookups[] = [];
  @Input() unreadMessages: Message[] | undefined;

  currentDate: Date = new Date();
  birthdayMinDate: Date = new Date();
  calendarNavigationDate: Date | undefined = new Date();


  userProfileComponentState: UserProfileComponentState = UserProfileComponentState.INITIAL;
  userProfileComponentStateType = UserProfileComponentState;
  editProfilePictureMode: boolean = false;
  profilePicture: string | undefined;
  hasProfile: boolean = true;

  inputForm = new FormGroup({
    firstname: new FormControl(),
    lastname: new FormControl(),
    gender: new FormControl(),
    city: new FormControl(),
    country: new FormControl(),
    birthDay: new FormControl(),
    phoneNumber: new FormControl(),
    tshirtSize: new FormControl(),
    address: new FormControl(),
    email: new FormControl(),
    zip: new FormControl(),
    picture: new FormControl(),
    category: new FormControl(),
    identityCardNumber: new FormControl(),
    identityCardIssuer: new FormControl(),
    identityCardExpires: new FormControl(),
    club: new FormControl(),
    emergencyPhone: new FormControl(),
    meal: new FormControl(),
    nickname: new FormControl()
  });

  constructor(private toastService: ToastService, private router: Router, private translocoService: TranslocoService,
              private myTraceyDataStore: MyTraceyDataStore,
              private loaderService: LoaderService,
              public formatter: NgbDateParserFormatter, private userDetailService:UserDetailService) {
    super();
    this.birthdayMinDate = this.getMinDateForBirthday();
  }

  ngOnInit(): void {
    this.hasProfile = !!(this.currentUserDetails && this.currentUserDetails.id);
    this.initForm();
    this.setFormValues()
  }

  private initForm() {
    this.inputForm.controls.firstname.addValidators([required, max_length_45, alphabetical]);
    this.inputForm.controls.lastname.addValidators([required, max_length_45, alphabetical]);
    this.inputForm.controls.birthDay.addValidators([required]);
    this.inputForm.controls.email.addValidators([required, email]);
    this.inputForm.controls.city.addValidators([max_length_45, alphabetical]);
    this.inputForm.controls.club.addValidators([max_length_100]);
    this.inputForm.controls.address.addValidators([max_length_50]);
    this.inputForm.controls.zip.addValidators([max_length_10]);
    this.inputForm.controls.nickname.addValidators([max_length_30]);
    this.inputForm.controls.picture.addValidators([mediumTextValidator]);
    this.inputForm.controls.identityCardNumber.addValidators([max_length_30]);
    this.inputForm.controls.identityCardIssuer.addValidators([max_length_255]);
  }

  private transformDate(initalDate: Date): Date {
    let date: Date = new Date(initalDate);
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  private setFormValues(): void {
    if (this.currentUserDetails?.picture) this.profilePicture = this.currentUserDetails.picture;
    if (this.currentUserDetails?.birthDay) {
      this.currentUserDetails!.birthDay = this.transformDate(this.currentUserDetails.birthDay);
    }
    else {
      this.calendarNavigationDate?.setFullYear(this.currentDate.getFullYear() - UserProfileEditComponent.BIRTHDAY_YEAR_SHIFT)
    }
    if (this.currentUserDetails?.identityCardExpires) {
      this.currentUserDetails!.identityCardExpires = this.transformDate(this.currentUserDetails.identityCardExpires);
    }
    if (this.currentUserDetails) {
      this.inputForm.patchValue(this.currentUserDetails);
    }
    if (this.inputForm.controls.email.value) {
      this.inputForm.controls.email.disable()
    }
    this.inputForm.updateValueAndValidity();
  }

  processFile(imageInput: any): void {
    if (imageInput.files?.length > 0) {
      const file: File = imageInput.files[0];
      if (file.size > environment.maxImageSize) {
        this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate("app.user.profile.max.size.error"));
      } else {
        const reader: FileReader = new FileReader();
        reader.addEventListener('load', (event: any) => {
          this.profilePicture = event.target.result;
        });
        reader.readAsDataURL(file);
      }
    }
    this.editProfilePictureMode = !this.editProfilePictureMode;
  }

  onEditProfilePicture(): void {
    this.editProfilePictureMode = !this.editProfilePictureMode;
  }

  onDeleteProfilePicture(): void {
    this.editProfilePictureMode = !this.editProfilePictureMode;
    this.profilePicture = undefined;
  }



  private getPersonalControls() {
    return ['firstname', 'lastname', 'gender', 'birthDay', 'email', 'phoneNumber', 'address', 'zip', 'city', 'country'];
  }

  private getOtherControls() {
    return ['club', 'nickname', 'emergencyPhone', 'identityCardNumber', 'identityCardExpires', 'identityCardIssuer', 'tshirtSize', 'meal'];
  }


  private isTabValid(controlNames: string[]): boolean {
    return controlNames.every(name => {
      const control = this.inputForm.get(name);
      if (!control || control.disabled) return true;
      return control.valid;
    });
  }

  private markTabAsTouched(controlNames: string[]): void {
    controlNames.forEach(name => this.inputForm.get(name)?.markAsTouched());
  }


  public savePersonalInfo(): void {
    const controls = this.getPersonalControls();
    if (!this.isTabValid(controls)) {
      this.markTabAsTouched(controls);
      return;
    }
    this.save(controls);
  }

  public saveOtherInfo(): void {
    const controls = this.getOtherControls();
    if (!this.isTabValid(controls)) {
      this.markTabAsTouched(controls);
      return;
    }
    this.save(controls);
  }

  public saveAll(): void {
    const personalControls = this.getPersonalControls();
    const otherControls = this.getOtherControls();

    if (!this.isTabValid(personalControls)) {
      this.markTabAsTouched(personalControls);
      return;
    }

    if (!this.isTabValid(otherControls)) {
      this.markTabAsTouched(otherControls);
      return;
    }

    this.save([...personalControls, ...otherControls], true);
  }


  private save(controlNames: string[], navigate?: boolean): void {
    const userDetails: UserDetail = this.getUserDetails(this.inputForm, controlNames);
    this.loaderService.startLoading();
    this.userDetailService.updateUserProfile(userDetails)
        .pipe(finalize(() => this.loaderService.dismissLoading()))
        .subscribe((userDetails: UserDetail | undefined) => {
          if (navigate) {
            this.toProfilePage()
          }
          else {
            this.currentUserDetails = userDetails;
            this.setFormValues()
          }
        });
  }


  getUserDetails(form: FormGroup, controlNames: string[]): UserDetail {
    const value = form.getRawValue();
    let userDetails: UserDetail = {...this.currentUserDetails};

    userDetails.picture = this.profilePicture;

    const include = (field: string) => controlNames.includes(field);

    if (include('address')) userDetails.address = value.address;
    if (include('country')) userDetails.country = value.country;
    if (include('firstname')) userDetails.firstname = value.firstname;
    if (include('gender')) userDetails.gender = value.gender;
    if (include('height')) userDetails.height = value.height;
    if (include('lastname')) userDetails.lastname = value.lastname;
    if (include('phoneNumber')) userDetails.phoneNumber = value.phoneNumber;
    if (include('city')) userDetails.city = value.city;
    if (include('tshirtSize')) userDetails.tshirtSize = value.tshirtSize;
    if (include('weight')) userDetails.weight = value.weight;
    if (include('identityCardIssuer')) userDetails.identityCardIssuer = value.identityCardIssuer;
    if (include('identityCardNumber')) userDetails.identityCardNumber = value.identityCardNumber;
    if (include('emergencyPhone')) userDetails.emergencyPhone = value.emergencyPhone;
    if (include('meal')) userDetails.meal = value.meal;
    if (include('transport')) userDetails.transport = value.transport;
    if (include('club')) userDetails.club = value.club;
    if (include('zip')) userDetails.zip = value.zip;
    if (include('footwearSize')) userDetails.footwearSize = value.footwearSize;
    if (include('nickname')) userDetails.nickname = value.nickname;
    if (include('birthDay')) {
      userDetails.birthDay = value.birthDay
          ? adjustForTimezone(this.transformDate(value.birthDay))
          : undefined;
    }
    if (include('identityCardExpires')) {
      userDetails.identityCardExpires = value.identityCardExpires
          ? adjustForTimezone(this.transformDate(value.identityCardExpires))
          : undefined;
    }

    return userDetails;
  }


  getMinDateForBirthday(): Date {
    const minDate = new Date(this.currentDate);
    minDate.setFullYear(this.currentDate.getFullYear() - 110);
    return minDate;
  }


  toProfilePage(): void {
    this.router.navigate([MY_TRACEY]);
  }
}

enum UserProfileComponentState {
  INITIAL,
  SUCCESS,
  ERROR,
}
